/* NPTA search suggestions.
 *
 * Progressive enhancement for every search form on the site: the header
 * dropdown, the search page's own bar, and anything added later. A form is
 * enhanced when it has role="search" and an input[name="q"]; opt out with
 * data-no-suggest on the form.
 *
 * Results come from Shopify's own /search/suggest.json, which is the index
 * the search results page queries, so a suggestion can never disagree with
 * what submitting the form actually returns. No second index to maintain.
 *
 * Nothing here is required for search to work. Without JS, without fetch, or
 * with the endpoint unreachable, every form stays exactly the plain GET form
 * it was.
 *
 * Per-form overrides, as data attributes:
 *   data-suggest-limit="6"            how many products to show
 *   data-suggest-view-all="View all results for"
 *   data-suggest-empty="No products match"
 */
(function () {
  'use strict';

  if (!window.fetch || !document.querySelectorAll || !window.AbortController) return;

  var SEL = 'form[role="search"]';
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;

  function esc(t) {
    return String(t).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    /* The shop's money format drops cents, but this endpoint returns a plain
       decimal string, so formatting here keeps the cents the rest of the
       store now shows. */
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Bold the typed term inside the title so the match is visible. */
  function mark(title, term) {
    var i = title.toLowerCase().indexOf(term.toLowerCase());
    if (i < 0) return esc(title);
    return esc(title.slice(0, i)) + '<mark>' + esc(title.slice(i, i + term.length)) +
           '</mark>' + esc(title.slice(i + term.length));
  }

  function thumb(product) {
    var url = product.featured_image && product.featured_image.url;
    if (!url) return '<span class="npta-sg__thumb"></span>';
    /* Ask the CDN for a thumbnail rather than the full-size original. */
    var small = url.replace(/(\.[a-z]+)(\?|$)/i, '_88x$1$2');
    return '<img class="npta-sg__thumb" src="' + esc(small) +
           '" alt="" loading="lazy" width="44" height="44">';
  }

  var uid = 0;

  function enhance(form) {
    var input = form.querySelector('input[name="q"]');
    if (!input || form.hasAttribute('data-no-suggest') || form.dataset.nptaSg === '1') return;
    form.dataset.nptaSg = '1';

    var id = 'npta-sg-' + (++uid);
    var limit = parseInt(form.getAttribute('data-suggest-limit'), 10) || 6;
    var viewAll = form.getAttribute('data-suggest-view-all') || 'View all results for';
    var noHits = form.getAttribute('data-suggest-empty') || 'No products match';

    var panel = document.createElement('div');
    panel.className = 'npta-sg';
    panel.id = id;
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');
    panel.hidden = true;

    /* Only the forms actually enhanced become positioning contexts. */
    form.classList.add('npta-sg-host');
    form.appendChild(panel);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);
    input.setAttribute('autocomplete', 'off');

    var items = [], active = -1, timer = null, controller = null;

    function close() {
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      items = []; active = -1;
    }

    function highlight(n) {
      if (!items.length) return;
      if (active > -1) items[active].classList.remove('is-active');
      active = (n + items.length) % items.length;
      items[active].classList.add('is-active');
      input.setAttribute('aria-activedescendant', items[active].id);
      items[active].scrollIntoView({ block: 'nearest' });
    }

    function render(products, term) {
      if (!products.length) {
        panel.innerHTML = '<p class="npta-sg__empty">' + esc(noHits) +
                          ' “' + esc(term) + '”</p>';
      } else {
        panel.innerHTML = products.map(function (p, i) {
          return '<a class="npta-sg__item" role="option" aria-selected="false" id="' +
            id + '-o' + i + '" href="' + esc(p.url) + '">' + thumb(p) +
            '<span class="npta-sg__text">' +
              '<span class="npta-sg__title">' + mark(p.title, term) + '</span>' +
              '<span class="npta-sg__price">' + money(p.price) + '</span>' +
            '</span></a>';
        }).join('') +
        '<button type="submit" class="npta-sg__foot">' + esc(viewAll) +
        ' “' + esc(term) + '”</button>';
      }
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      items = [].slice.call(panel.querySelectorAll('[role="option"]'));
      active = -1;
    }

    function lookup(term) {
      if (controller) controller.abort();
      controller = new AbortController();
      fetch('/search/suggest.json?q=' + encodeURIComponent(term) +
            '&resources[type]=product&resources[limit]=' + limit,
            { signal: controller.signal })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          /* A newer keystroke may have landed while this was in flight. */
          if (input.value.trim() !== term) return;
          render((d.resources && d.resources.results && d.resources.results.products) || [], term);
        })
        .catch(function () { /* aborted or offline: the form still submits */ });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(function () { lookup(term); }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
      else if (e.key === 'Enter' && active > -1) { e.preventDefault(); items[active].click(); }
      else if (e.key === 'Escape') { close(); }
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
  }

  function init() {
    [].slice.call(document.querySelectorAll(SEL)).forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The header and drawers re-render through the section rendering API, which
     replaces their markup and drops the listeners with it. */
  document.addEventListener('shopify:section:load', init);
}());
